/**
 * EmailContextStrip — one sentence of state for the currently selected email,
 * plus EXACTLY ONE primary action. When the host passes a catalog, a
 * "Start another workflow" picker sits under the primary so a wrong
 * recommendation is never a dead end. Without a catalog the legacy
 * "Launch another" secondary is kept for hosts that have not wired one.
 *
 * Shared by ora and the Outlook add-in (`@product-app/common/workflows/…`);
 * there is no forked copy under the add-in tree (design Constraint 20).
 *
 * The component is PRESENTATIONAL: it derives nothing. Its state, pill and
 * actions come from `deriveEmailContext`, so the binding copy table has one
 * consumer and a probe can drive this component straight from a captured
 * server response.
 */

import SelectMenu from "~/common/components/SelectMenu";
import React, { useState } from "react";
import type {
  EmailContextPrimaryAction,
  EmailContextSecondaryAction,
  EmailContextState,
} from "../emailContextState";
import { STATUS_PILL_BASE, STATUS_PILL_TONE } from "../statusPill";

export interface WorkflowOption {
  id: string;
  label: string;
}

export const START_ANOTHER_WORKFLOW_COPY = "Start another workflow";
export const START_A_WORKFLOW_COPY = "Start a workflow";

export interface EmailContextStripProps {
  state: EmailContextState;
  /** The BINDING pill copy, already resolved by `deriveEmailContext`. */
  pill: string;
  /**
   * The pill's tone from `deriveEmailContext` (white "Pending Email Send",
   * amber "Needs You"). Omitted: amber for `input_required`, calm grey else.
   */
  pillTone?: "needsYou" | "neutral" | "warning" | null;
  /** The pill's accessible name when its text alone reads wrong ("Completed*", R53). */
  pillAriaLabel?: string | null;
  primaryAction: EmailContextPrimaryAction | null;
  secondaryAction: EmailContextSecondaryAction | null;
  /** Raised to the host — the strip performs nothing itself. */
  onPrimaryAction?: (action: EmailContextPrimaryAction) => void;
  onSecondaryAction?: () => void;
  /** Disables both buttons while the host is acting on a previous press. */
  busy?: boolean;
  /** Subject of the selected mail item — shown when the host has one. */
  subject?: string | null;
  /** Sender of the selected mail item. */
  from?: string | null;
  /** One-line explanation under the subject (optional). */
  note?: string | null;
  /** Recommended workflow name shown above Review & Approve. */
  recommendedWorkflowLabel?: string | null;
  /** Catalog entries for the Start another workflow picker. */
  workflowOptions?: WorkflowOption[];
  /** Opens the launch form for the workflow picked in the dropdown. */
  onStartAnotherWorkflow?: (workflowId: string) => void;
}

/** "<label> — warnings need acknowledgement" plus " on <date>" when the visible pill carries one. */
function srPillText(pill: string, ariaLabel: string): string {
  const date = /\son\s(.+)$/.exec(pill)?.[1];
  return date ? `${ariaLabel} on ${date}` : ariaLabel;
}

export function EmailContextStrip({
  state,
  pill,
  pillTone,
  pillAriaLabel,
  primaryAction,
  secondaryAction,
  onPrimaryAction,
  onSecondaryAction,
  busy = false,
  subject,
  from,
  note,
  recommendedWorkflowLabel,
  workflowOptions,
  onStartAnotherWorkflow,
}: EmailContextStripProps): React.ReactElement {
  // While the lookup for the CURRENT item is in flight the strip shows the
  // resolving affordance and NOTHING else — no stale pill, no action. An
  // action offered before the state is known is an action offered for the
  // wrong email. The guard is here as well as in `deriveEmailContext` so a
  // caller that hands us both cannot render one.
  const resolving = state === "resolving" || state === "no_item";
  // Story 26741 AC-4: `no_action` offers no launch control BY DEFAULT, and the
  // catalog picker is a launch control — a prominent one. Suppressing it here
  // rather than at the host is what makes the rule hold for every host: ora
  // renders this same strip, and an override that is only unusual in Outlook
  // is not unusual. What remains is the underlined "Launch anyway" secondary
  // below, which is the deliberate way through.
  const declined = state === "no_action";
  const showPicker =
    !resolving &&
    !declined &&
    Array.isArray(workflowOptions) &&
    typeof onStartAnotherWorkflow === "function";
  const [pickedWorkflowId, setPickedWorkflowId] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const rawPrimary = resolving ? null : primaryAction;
  const secondary = resolving ? null : secondaryAction;
  // A catalog picker replaces "Run analysis". Review & Approve only shows
  // when we can name the recommended workflow — an empty label is a junk brief.
  const primary =
    rawPrimary == null
      ? null
      : rawPrimary.kind === "run_analysis" && showPicker
        ? null
        : rawPrimary.kind === "open_brief" && showPicker && !recommendedWorkflowLabel
          ? null
          : rawPrimary;
  const hasReadyBrief = Boolean(recommendedWorkflowLabel) && primary?.kind === "open_brief";
  const pickerPillLabel = hasReadyBrief
    ? START_ANOTHER_WORKFLOW_COPY
    : START_A_WORKFLOW_COPY;

  return (
    <div
      data-testid="email-context-strip"
      data-state={state}
      className="flex w-full flex-col gap-2 rounded-lg border border-primary-gray-light-300 bg-base-white px-3 py-3"
    >
      <div className="flex items-center gap-2">
        <p className="flex-1 text-[10px] font-semibold uppercase tracking-[0.07em] text-primary-gray-light-600">
          The email you are on
        </p>
        {/* aria-live so a screen reader announces the RE-RESOLUTION after the AM
            switches emails — the strip is replaced in place, with no focus move
            and no route change to announce it otherwise. */}
        <p
          aria-live="polite"
          title={pillAriaLabel ?? undefined}
          data-testid="email-context-strip-pill"
          className={`${STATUS_PILL_BASE} ${
            // A run waiting on the Servicer wears the shared status pill (amber
            // Needs You, or white Pending Email Send); every other state keeps
            // the calm grey one in the same shape.
            pillTone
              ? STATUS_PILL_TONE[pillTone]
              : state === "input_required"
                ? STATUS_PILL_TONE.needsYou
                : "border border-primary-gray-light-200 bg-primary-gray-light-50 text-primary-gray-light-700"
          }`}
        >
          {/* With an accessible name (Completed*), the visible pill is hidden from
              AT and the sr-only line says the same words WITH the date (an
              aria-label on a <p> is not reliably announced, and drops the date).
              Every other pill is read as is: hiding it would silence the live region. */}
          <span aria-hidden={pillAriaLabel ? true : undefined}>{pill}</span>
          {pillAriaLabel ? (
            <span className="sr-only">{srPillText(pill, pillAriaLabel)}</span>
          ) : null}
        </p>
      </div>

      {subject ? (
        <div className="flex flex-col gap-0.5">
          <p className="text-[13px] font-semibold leading-[19px] text-primary-gray-light-900">
            {subject}
          </p>
          {from ? (
            <p className="text-2xs leading-4 text-primary-gray-light-600">
              {from}
            </p>
          ) : null}
        </div>
      ) : null}

      {note ? (
        <p className="text-2xs leading-4 text-primary-gray-light-600">{note}</p>
      ) : null}

      {(primary || secondary || showPicker) && (
        <div className="flex flex-col gap-1.5">
          {primary && (
            <div className="flex flex-col gap-1">
              {recommendedWorkflowLabel ? (
                <p
                  data-testid="email-context-recommended-workflow"
                  className="text-[10px] font-semibold uppercase tracking-[0.07em] text-primary-gray-light-500"
                >
                  Recommended · {recommendedWorkflowLabel}
                </p>
              ) : null}
              <button
                type="button"
                disabled={busy}
                data-testid="email-context-primary-action"
                data-action-kind={primary.kind}
                onClick={() => onPrimaryAction?.(primary)}
                className="flex w-full items-center justify-center gap-2 rounded-md bg-sunrise px-3 py-2.5 text-[13px] font-semibold text-primary-gray-light-900 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-sunrise"
              >
                {primary.label}
                <span aria-hidden="true">→</span>
              </button>
            </div>
          )}
          {showPicker ? (
            <div
              className={
                primary
                  ? "mt-1 flex flex-col gap-1.5 border-t border-primary-gray-light-200 pt-2"
                  : "flex flex-col gap-1.5"
              }
              data-testid="email-context-start-another"
            >
              <button
                type="button"
                disabled={busy}
                aria-expanded={pickerOpen}
                aria-controls="email-context-start-another-panel"
                data-testid="email-context-start-another-pill"
                onClick={() => setPickerOpen((open) => !open)}
                className="flex w-full items-center justify-between gap-2 text-left disabled:opacity-60"
              >
                <span className="text-xs font-medium text-primary-gray-light-700">
                  {pickerPillLabel}
                </span>
                <span
                  aria-hidden="true"
                  className="inline-block flex-shrink-0 text-primary-gray-light-400 transition-transform duration-200"
                  style={{
                    transform: pickerOpen ? "rotate(90deg)" : "rotate(0deg)",
                  }}
                >
                  ›
                </span>
              </button>
              {pickerOpen ? (
                <div
                  id="email-context-start-another-panel"
                  className="flex items-center gap-2"
                >
                  {/* SelectMenu, not a native <select> — see the component
                      header. Same reason the Oversight picker moved. */}
                  <SelectMenu
                    data-testid="email-context-start-another-select"
                    label={START_ANOTHER_WORKFLOW_COPY}
                    disabled={busy}
                    value={pickedWorkflowId}
                    onChange={setPickedWorkflowId}
                    placeholder="Choose a workflow"
                    options={(workflowOptions ?? []).map((opt) => ({
                      value: opt.id,
                      label: opt.label,
                    }))}
                    className="min-w-0 flex-1"
                  />
                  <button
                    type="button"
                    disabled={busy || !pickedWorkflowId}
                    data-testid="email-context-start-another-go"
                    aria-label="Start selected workflow"
                    onClick={() => {
                      if (!pickedWorkflowId) return;
                      onStartAnotherWorkflow?.(pickedWorkflowId);
                    }}
                    className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-sunrise text-primary-gray-light-900 disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-sunrise"
                  >
                    <span aria-hidden="true">→</span>
                  </button>
                </div>
              ) : null}
            </div>
          ) : secondary ? (
            <button
              type="button"
              disabled={busy}
              data-testid="email-context-secondary-action"
              onClick={() => onSecondaryAction?.()}
              className="w-full text-center text-2xs leading-4 text-primary-gray-light-600 underline disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-sunrise"
            >
              {secondary.label}
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default EmailContextStrip;
